import React from 'react'
import { Helmet } from 'react-helmet'

// SEO Component
const SEO = ({ title, description, keywords, canonical, noindex = false, ogImage }) => {
  const fullTitle = title ? `${title} | Pulpit Cab` : 'Pulpit Cab'
  const image = ogImage || 'https://pulpitcabs.com/logo512.png'

  return (
    <Helmet defer={false}>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {canonical && <link rel="canonical" href={canonical} />}
      {noindex && <meta name="robots" content="noindex, follow" />}
      <meta property="og:site_name" content="Pulpit Cabs" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />
    </Helmet>
  )
}

// SEO Data Function
export function getSEOData(pageName, cityName = '') {
  // City-specific SEO data
  if (pageName === 'city' && cityName) {
    const citySEO = {
      'Ahmedabad': {
        title: `Cab Service in ${cityName} - AC Cabs, Verified Drivers, Best Deals | Pulpit`,
        description: `Book trusted cab service in ${cityName} with Pulpit. 24x7 taxi booking for local travel, airport pickups, and outstation trips. Clean cabs, verified drivers, easy online booking.`,
        keywords: `taxi in ${cityName}, cabs in ${cityName}, outstation cabs ${cityName}, ${cityName} airport taxi, cab service in ${cityName}, car hire in ${cityName}, taxi service in ${cityName}, ${cityName} airport cab, car rental in ${cityName}, ${cityName} taxi, ${cityName} sightseeing by car, ${cityName} car rental, cab booking ${cityName}, taxi booking ${cityName}, ${cityName} taxi service, ${cityName} cabs in india, ${cityName} cab service in india, ${cityName} cab service, outstation cab booking ${cityName}, outstation taxi service ${cityName}, outstation cab service ${cityName}, ${cityName} taxi booking service, outstation taxi ${cityName}, book taxi for outstation ${cityName}, online outstation cab booking ${cityName}, book a outstation cab ${cityName}, ${cityName} taxi service near me, ${cityName} taxi near me, ${cityName} cab service near me, ${cityName} cabs near me, full day taxi in ${cityName}`
      }
    };
    
    return citySEO[cityName] || {
      title: `Cab Service in ${cityName} - AC Cabs, Verified Drivers, Best Deals | Pulpit`,
      description: `Book trusted cab service in ${cityName} with Pulpit. 24x7 taxi booking for local travel, airport pickups, and outstation trips. Clean cabs, verified drivers, easy online booking. Starting from ₹9/km.`,
      keywords: `taxi in ${cityName}, cabs in ${cityName}, outstation cabs ${cityName}, ${cityName} airport taxi, cab service in ${cityName}, car hire in ${cityName}, taxi service in ${cityName}, ${cityName} airport cab, car rental in ${cityName}, ${cityName} taxi, ${cityName} sightseeing by car, ${cityName} car rental, cab booking ${cityName}, taxi booking ${cityName}, ${cityName} taxi service, ${cityName} cabs in india, ${cityName} cab service in india, ${cityName} cab service, outstation cab booking ${cityName}, outstation taxi service ${cityName}, outstation cab service ${cityName}, ${cityName} taxi booking service, outstation taxi ${cityName}, book taxi for outstation ${cityName}, online outstation cab booking ${cityName}, book a outstation cab ${cityName}, ${cityName} taxi service near me, ${cityName} taxi near me, ${cityName} cab service near me, ${cityName} cabs near me, full day taxi in ${cityName}`
    };
  }

  const seoData = {
    home: {
      title: "Pulpit - Your One-Stop Cab Booking Solution",
      description: "Book cabs easily with Pulpit for in-city, outstation trips, or hourly rentals. Our convenient app connects you to reliable rides in just one click.",
      keywords: "cab booking, oneway taxi, outstation cabs, hourly rentals, Pulpit cabs"
    },
    partner: {
      title: "Drive & Earn with Pulpit | Attach Car to Cab Company | Zero Commission",
      description: "Join Pulpit as a cab driver or fleet owner. Attach your car, earn daily with zero commission. Free registration across 450+ cities in India. Start your cab driver job today.",
      keywords: "drive and earn, attach car to cab company, cab driver job, join as cab driver, zero commission cab, cab partner registration, fleet owner India, cab driver registration online, attach vehicle, daily payment cab driver, no commission cab platform, Pulpit partner"
    },
    corporate: {
      title: "Pulpit Corporate - Efficient Corporate Travel Solutions",
      description: "Streamline your company's travel needs with Pulpit Corporate. Manage employee rides, travel itineraries, and cost-effective cab solutions.",
      keywords: "corporate cab booking, corporate travel, company cab service, Pulpit corporate"
    },
    about: {
      title: "About Pulpit - Your Cab is a Click Away",
      description: "Need a ride? Pulpit offers in-city, outstation, and hourly cab rentals. Learn more about our mission to make cab booking as easy as one click.",
      keywords: "about Pulpit, cab booking service, Pulpit mission, on-demand taxi"
    },
    support: {
      title: "Pulpit Support - We're Here to Help",
      description: "Need help with cab bookings, payments, or app issues? Contact Pulpit Support for assistance with your queries and enjoy a hassle-free experience.",
      keywords: "Pulpit support, contact Pulpit, help with cab booking, customer support"
    },
    blog: {
      title: "Pulpit Blog - News, Updates, and Travel Tips",
      description: "Read the latest updates, travel tips, and industry trends on the Pulpit blog. Stay informed and make your cab booking experience even better.",
      keywords: "Pulpit blog, travel tips, industry updates, cab booking news"
    },
    app: {
      title: "Download the Pulpit App - Easy Cab Booking at Your Fingertips",
      description: "Download the Pulpit app for seamless cab booking. Whether for in-city trips or outstation travel, the Pulpit app offers easy and fast ride bookings.",
      keywords: "Pulpit app, download cab app, taxi booking app, fast cab booking"
    },
    agentLogin: {
      title: "Agent Login - Access Your Pulpit Partner Dashboard",
      description: "Login to your Pulpit Partner account to manage bookings, track earnings, and update vehicle details. Enjoy easy access to your driver or agent profile.",
      keywords: "Pulpit agent login, driver login, partner dashboard, manage bookings"
    },
    memberLogin: {
      title: "Member Login - Access Your Pulpit Account",
      description: "Login to your Pulpit account to manage your rides, view booking history, and update your profile details. Enjoy a personalized cab booking experience.",
      keywords: "Pulpit member login, account login, cab booking history, profile management"
    },
    forgotPassword: {
      title: "Forgot Password - Reset Your Pulpit Account Password",
      description: "Reset your Pulpit account password quickly and securely. Regain access to your profile and manage your bookings with ease.",
      keywords: "forgot password, reset Pulpit password, account recovery, password reset"
    },
    default: {
      title: "Pulpit - Cab Booking Made Easy",
      description: "Welcome to Pulpit, your go-to cab booking platform for in-city, outstation, and hourly cab rentals. Enjoy easy and quick ride bookings with Pulpit.",
      keywords: "cab booking, taxi service, outstation cabs, Pulpit rides"
    }
  };

  return seoData[pageName] || seoData.default;
}

export default SEO

